import { Link } from 'react-router-dom';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import BackToTop from '@/components/feature/BackToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import JsonLd from '@/components/feature/JsonLd';
import GroupCard from '@/pages/community/components/GroupCard';
import { communityGroups } from '@/mocks/gossip';

const steps = [
  { title: '复制群号或分享码', desc: '在上方卡片中点击复制，保存到你方便查看的地方。', icon: 'ri-file-copy-2-line' },
  { title: '打开对应平台搜索', desc: '微信群请通过群二维码添加，QQ 群直接搜索群号即可。', icon: 'ri-search-eye-line' },
  { title: '填写来源说明', desc: '申请时注明来自爆瓜福利，方便管理员快速通过审核。', icon: 'ri-edit-2-line' },
  { title: '进群一起聊', desc: '进群后可查看每日分享帖与专题链接，记得遵守群规。', icon: 'ri-chat-smile-2-line' },
];

const rules = [
  { icon: 'ri-shield-check-line', text: '不发布未经证实的个人信息与隐私内容。' },
  { icon: 'ri-forbid-2-line', text: '禁止在群内发布广告、外链与违法信息。' },
  { icon: 'ri-emotion-happy-line', text: '理性讨论，尊重当事人，不进行人身攻击。' },
  { icon: 'ri-error-warning-line', text: '发现违规内容可随时联系管理员处理。' },
];

export default function Community() {
  return (
    <div className="page-enter min-h-screen bg-background-50">
      <title>分享社区 - 分享群 / 分享链接 / 分享码合集 | 爆瓜福利</title>
      <meta
        name="description"
        content="爆瓜福利分享社区汇总当前开放的分享群、分享链接与分享码资源，附详细的加入步骤与群规说明，帮助您快速加入社区获取每日更新的吃瓜合集。"
      />
      <meta name="keywords" content="爆瓜福利分享群,爆瓜福利分享链接,爆瓜福利分享码,爆瓜福利分享资源,分享社区" />
      <JsonLd
        data={{
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: '爆瓜福利分享社区',
          description: '爆瓜福利分享群、分享链接与分享码资源合集。',
          inLanguage: 'zh-CN',
          breadcrumb: {
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: '/' },
              { '@type': 'ListItem', position: 2, name: '分享社区', item: '/community' },
            ],
          },
        }}
      />

      <SiteHeader />
      <main className="mx-auto w-full max-w-6xl px-4 py-10 md:px-6 md:py-14">
        <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '分享社区' }]} />

        <header className="mt-6 max-w-3xl border-b-2 border-foreground-950 pb-6">
          <h1 className="font-heading text-3xl font-bold text-foreground-950 md:text-4xl">分享社区</h1>
          <p className="mt-3 text-sm leading-7 text-foreground-600">
            爆瓜福利的分享帖、分享群、分享链接与分享码都汇总在这里。选择一个你习惯的平台加入，就能每天收到我们整理的吃瓜合集，再也不用担心错过热点。
          </p>
        </header>

        <section className="mt-10">
          <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
            <i className="ri-group-2-line text-primary-600"></i>
            当前开放的分享入口
          </h2>
          <div className="mt-5 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {communityGroups.map((group) => (
              <GroupCard key={group.name} group={group} />
            ))}
          </div>
        </section>

        <section className="mt-14 rounded-lg border border-background-200 bg-background-100 p-6 md:p-8">
          <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
            <i className="ri-route-line text-primary-600"></i>
            四步加入分享群
          </h2>
          <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {steps.map((step, index) => (
              <div key={step.title} className="relative rounded-lg bg-background-50 p-5">
                <span className="absolute right-4 top-4 font-heading text-3xl font-bold text-background-300">
                  0{index + 1}
                </span>
                <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-500 text-background-50">
                  <i className={`${step.icon} text-xl`}></i>
                </span>
                <h3 className="mt-4 font-heading text-sm font-bold text-foreground-950">{step.title}</h3>
                <p className="mt-2 text-xs leading-6 text-foreground-600">{step.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-14 grid gap-8 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
              <i className="ri-file-list-3-line text-primary-600"></i>
              分享链接与分享码说明
            </h2>
            <div className="mt-5 space-y-4 text-sm leading-8 text-foreground-700">
              <p>
                <strong className="text-foreground-950">分享链接</strong>
                是我们为每期专题整理的访问入口，通常以合集形式呈现，收录该周值得一看的内容，链接设有有效期，到期后会在本页统一更新。
              </p>
              <p>
                <strong className="text-foreground-950">分享码</strong>
                是一串短期有效的邀请码，用于访问限时专题或加入特定群组。分享码每日限量发放，请勿在公开渠道二次传播，避免被滥用。
              </p>
              <p>
                我们只收录原创内容与正规来源的公开信息，不会提供任何需要付费或无授权的内容。若发现异常链接，欢迎通过
                <Link to="/about" className="mx-1 text-primary-700 underline">
                  关于我们
                </Link>
                页面联系反馈。
              </p>
            </div>
          </div>

          <div className="rounded-lg border border-background-200 bg-background-50 p-6">
            <h2 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
              <i className="ri-parentheses-line text-primary-600"></i>
              社群公约
            </h2>
            <ul className="mt-4 space-y-4">
              {rules.map((rule) => (
                <li key={rule.text} className="flex items-start gap-3 text-xs leading-6 text-foreground-600">
                  <i className={`${rule.icon} mt-0.5 text-base text-accent-600`}></i>
                  {rule.text}
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="mt-14 flex flex-col items-center justify-between gap-5 rounded-lg bg-foreground-950 px-6 py-8 text-center sm:flex-row sm:text-left md:px-10">
          <div>
            <h2 className="font-heading text-lg font-bold text-background-50">还想了解更多？</h2>
            <p className="mt-2 text-sm text-background-100/80">常见问题里整理了加入社群、分享码与内容更新的详细说明。</p>
          </div>
          <Link
            to="/faq"
            className="whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            查看常见问题
          </Link>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}